import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { hostApi } from "@/lib/hostApi";
import { useHostAuth } from "@/contexts/HostAuthContext";
import { toast } from "sonner";
import { Lightning, Crown, CheckCircle } from "@phosphor-icons/react";

/**
 * /host/setup/:setupToken — Landing page for a private host invite link.
 * Flow:
 *   1. Load invite preview (email, name, org).
 *   2. Host sets a password + optional profile fields.
 *   3. Submit → host_session_token issued → redirect to /host/console.
 */
export default function HostSetup() {
  const { setupToken } = useParams();
  const navigate = useNavigate();
  const { setupComplete } = useHostAuth();

  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [org, setOrg] = useState("");
  const [eventType, setEventType] = useState("");
  const [brandImageUrl, setBrandImageUrl] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    hostApi.get(`/live/host/invite/${setupToken}`)
      .then((r) => {
        setPreview(r.data);
        setOrg(r.data.org || "");
        setEventType(r.data.event_type || "");
      })
      .catch((e) => setError(e.response?.data?.detail || "Invalid or expired setup link"));
  }, [setupToken]);

  const canSubmit = password.length >= 8 && password === confirm && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await hostApi.post("/live/host/setup", {
        setup_token: setupToken,
        password,
        org: org || null,
        event_type: eventType || null,
        brand_image_url: brandImageUrl || null,
      });
      setupComplete(data);
      toast.success("Welcome — your Host Console is ready.");
      navigate("/host/console", { replace: true });
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Setup failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-nk-bg text-nk-text flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 text-nk-gold mb-2">
            <Crown size={18} weight="fill" />
            <span className="font-mono text-xs uppercase tracking-[0.2em]">NKognito Live</span>
          </div>
          <h1 className="font-display text-3xl font-medium">Host setup</h1>
          <p className="text-sm text-nk-mute mt-1">
            Finish your account to start running live events.
          </p>
        </div>

        {error && (
          <div className="nk-card p-5 border border-nk-bad/40 text-center" data-testid="host-setup-error">
            <p className="text-sm text-nk-bad">{error}</p>
            <p className="text-xs text-nk-mute mt-2">Ask an NKognito admin to reissue your invite.</p>
          </div>
        )}

        {!error && !preview && (
          <div className="nk-card p-8 text-center">
            <p className="text-sm text-nk-mute">Loading your invite…</p>
          </div>
        )}

        {preview && !preview.already_setup && (
          <form onSubmit={submit} className="nk-card p-6 space-y-4" data-testid="host-setup-form">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-nk-gold/70 font-mono mb-1">Invited as</p>
              <p className="text-sm font-medium">{preview.name}</p>
              <p className="text-xs text-nk-mute">{preview.email}</p>
            </div>

            <div>
              <label className="block text-xs text-nk-mute mb-1">Organization (optional)</label>
              <input
                data-testid="host-setup-org"
                value={org}
                onChange={(e) => setOrg(e.target.value)}
                className="w-full px-3 py-2 rounded-sm border border-nk-line bg-nk-charcoal text-sm focus:border-nk-gold outline-none"
                placeholder="Company, school, community…"
              />
            </div>

            <div>
              <label className="block text-xs text-nk-mute mb-1">Event type (optional)</label>
              <select
                data-testid="host-setup-event-type"
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
                className="w-full px-3 py-2 rounded-sm border border-nk-line bg-nk-charcoal text-sm focus:border-nk-gold outline-none"
              >
                <option value="">Select…</option>
                <option value="corporate">Corporate</option>
                <option value="school">School / campus</option>
                <option value="bar">Bar / pub</option>
                <option value="community">Community</option>
                <option value="wedding">Wedding / party</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-xs text-nk-mute mb-1">Brand image URL (optional)</label>
              <input
                data-testid="host-setup-brand-image"
                value={brandImageUrl}
                onChange={(e) => setBrandImageUrl(e.target.value)}
                className="w-full px-3 py-2 rounded-sm border border-nk-line bg-nk-charcoal text-sm focus:border-nk-gold outline-none"
                placeholder="https://…/logo.png"
              />
            </div>

            <div className="pt-2 border-t border-nk-line/40">
              <label className="block text-xs text-nk-mute mb-1">Set a password</label>
              <input
                data-testid="host-setup-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                required
                className="w-full px-3 py-2 rounded-sm border border-nk-line bg-nk-charcoal text-sm focus:border-nk-gold outline-none"
                placeholder="At least 8 characters"
              />
            </div>

            <div>
              <label className="block text-xs text-nk-mute mb-1">Confirm password</label>
              <input
                data-testid="host-setup-confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                className="w-full px-3 py-2 rounded-sm border border-nk-line bg-nk-charcoal text-sm focus:border-nk-gold outline-none"
              />
              {confirm && confirm !== password && (
                <p className="text-[11px] text-nk-bad mt-1">Passwords don&apos;t match</p>
              )}
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="host-setup-submit"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-sm bg-nk-gold text-nk-bg font-medium hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Lightning size={16} weight="fill" />
              {busy ? "Setting up…" : "Enter Host Console"}
            </button>
          </form>
        )}

        {preview?.already_setup && (
          <div className="nk-card p-6 space-y-3 text-center">
            <CheckCircle size={28} weight="fill" className="mx-auto text-nk-gold" />
            <p className="text-sm">You&apos;ve already set up this account.</p>
            <button
              onClick={() => navigate("/host/login")}
              className="text-sm text-nk-gold hover:underline"
              data-testid="host-setup-go-login"
            >
              Log in to your Host Console →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
